<script setup>
// "Ocupado" com motivo: quando a conta tem motivos de pausa ativos, escolher
// Ocupado no menu de status abre este diálogo e a pausa só sai com um motivo —
// senão o relatório enche de "Ocupado" sem dizer o que foi. Conta sem motivos
// cadastrados nem abre: o menu pausa direto, como sempre foi.
//
// Montado UMA vez na barra superior (fora do dropdown do perfil, que desmonta
// ao fechar); quem abre é a flag `isReasonDialogOpen` do store.
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import { useAgentPause } from './useAgentPause';

const { t } = useI18n();
const store = useStore();
const uiFlags = useMapGetter('agentPauses/getUIFlags');
const { options, reason, pause, optionLimitLabel, isChanging } =
  useAgentPause();

const dialogRef = ref(null);
const form = reactive({ reason: '' });

const rules = {
  reason: {
    required: helpers.withMessage(
      () => t('AGENT_PAUSE.REASON_DIALOG.REQUIRED'),
      required
    ),
  },
};
const v$ = useVuelidate(rules, form);

// "Almoço · 1h05" — o limite já aparece na hora de escolher.
const comboOptions = computed(() =>
  options.value.map(option => {
    const limit = optionLimitLabel(option);
    return {
      value: option.value,
      label: limit ? `${option.label} · ${limit}` : option.label,
    };
  })
);

const reasonError = computed(() =>
  v$.value.reason.$error ? v$.value.reason.$errors[0]?.$message : ''
);

watch(
  () => uiFlags.value.isReasonDialogOpen,
  isOpen => {
    if (!isOpen) return;
    // Já pausado: começa no motivo atual (troca de motivo).
    form.reason = reason.value || '';
    v$.value.$reset();
    dialogRef.value?.open();
  }
);

// Motivo escolhido saiu da lista (arquivado e recarregado depois do 422):
// limpa, senão o campo mostra vazio e o Pausar reenviaria a key morta.
watch(comboOptions, list => {
  if (form.reason && !list.some(option => option.value === form.reason)) {
    form.reason = '';
  }
});

const onClose = () => store.dispatch('agentPauses/closeReasonDialog');

const onConfirm = async () => {
  if (!(await v$.value.$validate())) return;
  // Falhou (ex.: motivo arquivado nesse meio-tempo): fica aberto, com a
  // lista já recarregada, pro agente escolher outro.
  if (await pause(form.reason)) dialogRef.value?.close();
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :title="t('AGENT_PAUSE.REASON_DIALOG.TITLE')"
    :description="t('AGENT_PAUSE.REASON_DIALOG.DESCRIPTION')"
    :confirm-button-label="t('AGENT_PAUSE.REASON_DIALOG.CONFIRM')"
    :cancel-button-label="t('AGENT_PAUSE.REASON_DIALOG.CANCEL')"
    :is-loading="isChanging"
    @confirm="onConfirm"
    @close="onClose"
  >
    <ComboBox
      v-model="form.reason"
      :options="comboOptions"
      :placeholder="t('AGENT_PAUSE.REASON_DIALOG.PLACEHOLDER')"
      :search-placeholder="t('AGENT_PAUSE.REASON_DIALOG.SEARCH')"
      :has-error="Boolean(reasonError)"
      :message="reasonError"
    />
  </Dialog>
</template>
